<script setup lang="ts">
import { FOOTER_LINKS } from './FooterData'
import { airportsOrder, branchesOrder } from '@/data/branchesPageData'

const i18nService = usei18nService()
const { locale, t } = useI18n()
const localePath = useLocalePath()

const links = FOOTER_LINKS

const CMSLocationsStore = useCMSLocationsStore()
const CMSLocations = ref<ICMSLocationModel[] | undefined>(undefined)
// Turns off the global footer skeleton (assets/scss/components/_footer-skeleton.scss) once styles and locations are in place
const isReady = ref(false)

onMounted(async () => {
	try {
		CMSLocations.value = await CMSLocationsStore.fetchCMSLocationsForScope('footer')
	} finally {
		isReady.value = true
	}
})

const getURLforLocation = (item: ICMSLocationModel) => {
	const loc = locale.value.toUpperCase() as 'PL' | 'EN'
	const urlLocale = item[`isActive${loc}`] ? item[`url${loc}`] : undefined
	const url = item.urlPL

	if (!url && !urlLocale) return undefined
	else
		return urlLocale
			? localePath({ name: 'car-rental-urlSlug', params: { urlSlug: urlLocale } }, locale.value)
			: localePath({ name: 'car-rental-urlSlug', params: { urlSlug: url } }, 'pl')
}

const itemNameLabel = (item: ICMSLocationModel) => {
	const loc = locale.value.toUpperCase() as 'PL' | 'EN'
	return item[`name${loc}`] ?? item.namePL
}

const sortByOrder = (order: string[]) => (a: ICMSLocationModel, b: ICMSLocationModel) => {
	const rank = (l: ICMSLocationModel) => {
		const index = order.indexOf(l.urlPL)
		return index === -1 ? order.length : index
	}
	return rank(a) - rank(b)
}

const formatLocations = (filterFn: (l: ICMSLocationModel) => boolean, sortFn: (a: ICMSLocationModel, b: ICMSLocationModel) => number) => {
	const filtered = CMSLocations.value?.filter(filterFn)
	if (!filtered?.length) return []
	return filtered.sort(sortFn).map((item: ICMSLocationModel) => ({
		to: getURLforLocation(item),
		label: t('components.footer.footer.car_rental_city', { city: itemNameLabel(item) }),
		preLocalized: true,
	}))
}

const airportsData = computed(() => formatLocations((l) => l.icon === 'airplane' && !l.otherLocation, sortByOrder(airportsOrder)))

const branchesData = computed(() => formatLocations((l) => l.icon !== 'airplane' && !l.otherLocation, sortByOrder(branchesOrder)))

const otherLocationsData = computed(() =>
	formatLocations(
		(l) => l.otherLocation,
		(a, b) => itemNameLabel(a).localeCompare(itemNameLabel(b)),
	),
)

const hidden = ref(true)

function show() {
	hidden.value = false
}

function hide() {
	hidden.value = true
}

const resolvedLinks = computed(() =>
	links.map((item) => {
		if (item.translation_key_title === 'our_branches') {
			return branchesData.value?.length > 0 ? branchesData.value : undefined
		} else if (item.translation_key_title === 'renting_at_the_airport') {
			return airportsData.value?.length > 0 ? airportsData.value : undefined
		} else if (item.translation_key_title === 'onther_locations_in_poland') {
			return otherLocationsData.value?.length > 0 && locale.value !== 'en' ? otherLocationsData.value : undefined
		} else {
			return item.links
		}
	}),
)
</script>

<template>
	<Head>
		<meta name="format-detection" content="telephone=no" />
	</Head>
	<footer class="global-footer" :class="{ 'global-footer--ready': isReady }">
		<div class="global-footer__container">
			<GlobalIcon class="global-footer__logo" name="carfree-logo-white" :width="111" :height="55" />
			<div class="global-footer__info-language-newsletter">
				<div>
					<p class="global-footer__company-name">CarFree Sp. z o.o.</p>
					<p class="global-footer__company-info">{{ $t('components.footer.footer.nip') }}: 521 369 56 26</p>
					<p class="global-footer__company-info">
						{{ $t('components.footer.footer.cybernetyki_5_st') }}<br />02-677 {{ $t('components.footer.footer.warsaw') }}
					</p>
					<div class="global-footer__social">
						<a
							class="global-footer__social-item"
							href="https://www.facebook.com/CarFree.Wypozyczalnia.Aut"
							rel="nofollow noopener"
							target="_blank"
							aria-label="CarFree na Facebooku">
							<GlobalIcon name="facebook" :width="20" />
						</a>
						<a
							class="global-footer__social-item"
							href="https://www.instagram.com/carfree.wypozyczalnia"
							rel="nofollow noopener"
							target="_blank"
							aria-label="CarFree na Instagramie">
							<GlobalIcon name="instagram" :width="20" />
						</a>
						<a
							class="global-footer__social-item"
							href="https://www.tiktok.com/@carfree.pl"
							rel="nofollow noopener"
							target="_blank"
							aria-label="CarFree na TikToku">
							<GlobalIcon name="tiktok" :width="20" />
						</a>
					</div>
				</div>
				<!-- TODO: uncomment when newsletter is ready -->
				<div class="global-footer__language-newsletter">
					<GlobalFooterNewsletter />
				</div>
				<!-- <GlobalFooterLanguageSwitcher /> -->
			</div>
			<GlobalFooterContact />
			<GlobalFooterLinksAndApp />
			<div class="global-footer__links-lists-wrapper" :data-hidden="hidden">
				<template v-for="(item, idx) in links" :key="item.translation_key_title || item.title || idx">
					<GlobalFooterLinksList
						v-if="resolvedLinks[idx]"
						:title="i18nService.$tDynamic('components.footer.links_and_app.links', item.translation_key_title || item.title)"
						:data="resolvedLinks[idx]" />
				</template>
				<template v-if="otherLocationsData.length > 4">
					<div class="global-footer__gradient"></div>
					<button class="global-footer__btn global-footer__btn--show" @click="show">{{ $t('components.footer.footer.show_more') }}</button>
					<div class="global-footer__btn-container">
						<button class="global-footer__btn" @click="hide">{{ $t('components.footer.footer.show_less') }}</button>
					</div>
				</template>
			</div>
			<GlobalFooterTerms />
		</div>
	</footer>
</template>

<style scoped lang="scss" src="./Footer.scss"></style>
